/**
 * @fileoverview Welcome / Onboarding — slider de 3 telas no 1º acesso.
 *
 * Substitui a antiga tela única de venda (3 cards de escolha) por um
 * carrossel de 3 slides que o usuário lê no PRIMEIRO acesso ao app naquele
 * device. Desenho: docs/specs/onboarding-entrada-tv-ux.md §2.
 *
 * - 3 slides (1 ideia cada): o que é → por que logar → como começar.
 * - Navegação dupla (TV-first): setas ←/→ trocam de slide E botões focáveis
 *   Voltar / Próximo / Começar. "Pular" discreto (não foco inicial).
 *   A REGRA das setas (uma só, sem exceção) e os guards de auto-repeat moram em
 *   `welcome-nav.ts`, puros e testados — inclusive o motivo de `→` no último
 *   slide ser BOUNCE e não "finalizar".
 * - 3 dots de progresso (não-focáveis — status, não controle).
 * - "visto" = terminou (Começar no slide 3) OU pulou → markWelcomeSeen().
 *   Avançar entre slides NÃO marca (sair no meio e voltar revê o resto).
 *
 * DESTINO do "Começar"/"Pular": leva ao GATE DE CONTA OBRIGATÓRIA (I5) —
 * `/account/gate`. O antigo caminho anônimo (`/onboarding/mode`) saiu do boot:
 * conta Swipe é obrigatória pra tudo. O slider só apresenta o produto e entrega
 * no gate (QR de "experimentar grátis").
 *
 * Aparece só na 1ª abertura (gated por welcome-flag.ts `iptv:welcome-seen`,
 * por-device). RootRoute pula quando a flag está setada.
 */

import { useTranslation } from '@app-iptv/i18n'
import { Focusable, setFocus } from '@app-iptv/ui'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppLogo } from '../components/AppLogo'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { IconArrowLeft, IconCheck, IconNext } from '../components/PlayerIcons'
import { WelcomeScene } from '../components/WelcomeScenes'
import { useAppExit } from '../hooks/useAppExit'
import { useBackKey } from '../hooks/useBackKey'
import { useFocusActivation } from '../hooks/useFocusActivation'
import { markWelcomeSeen } from '../welcome-flag'

import {
  createActivationTracker,
  FINISH_ARM_MS,
  nowMs,
  resolveArrow,
  SLIDE_COUNT,
  type ArrowDir,
  type WelcomeButton,
} from './welcome-nav'
import styles from './WelcomeScreen.module.css'

const NEXT_KEY = 'onboarding-next'
const BACK_KEY = 'onboarding-back'
const SKIP_KEY = 'onboarding-skip'
const FOCUS_KEY: Record<WelcomeButton, string> = {
  next: NEXT_KEY,
  back: BACK_KEY,
  skip: SKIP_KEY,
}

export function WelcomeScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const focusActive = useFocusActivation()
  const [slide, setSlide] = useState(0)
  const [confirmExit, setConfirmExit] = useState(false)
  const { requestExit, exitFailed, dismissExitFailed } = useAppExit()

  const isLast = slide === SLIDE_COUNT - 1
  const isFirst = slide === 0

  // ===== Gesto deliberado vs. auto-repeat (a máquina inteira em welcome-nav.ts).
  // Ref (não state): é lido dentro do handler de tecla, no mesmo tick do evento,
  // e não pode depender de re-render nem da ordem de effects do Norigin.
  const armRef = useRef(createActivationTracker())
  const lastBackAtRef = useRef(Number.NEGATIVE_INFINITY)

  // CAPTURA (3º argumento `true`) é OBRIGATÓRIO, não estilo: o listener do
  // Norigin que dispara onEnterPress/onArrowPress corre na fase de BUBBLE, então
  // só na captura este aqui roda ANTES e a decisão já enxerga o keydown atual.
  // Só marca ref — sem preventDefault, sem stopPropagation, zero interferência.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      armRef.current.keyDown(nowMs(), e.repeat, e.key)
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [])

  /**
   * "Este BACK/cancelamento é gesto de verdade?" — porta única, usada tanto pelo
   * handler de BACK desta tela quanto pelo `onCancel` dos dialogs.
   *
   * O onCancel PRECISA passar por aqui porque o `ConfirmDialog` registra o
   * PRÓPRIO `useBackKey` (ConfirmDialog.tsx:116), sem gate: com o dialog aberto,
   * TODA repetição de BACK chegava lá e fechava o dialog num listener que este
   * guard não alcança. Guard correto, dialog correto, defeito na FRONTEIRA — o
   * sintoma era o dialog de sair piscando ~90ms a cada 1,2s com o BACK preso.
   *
   * A saída por TEMPO é contrato, não conveniência: o comentário do próprio
   * ConfirmDialog diz que "nenhum modal deste app pode ser inescapável". Mesmo
   * em firmware que mente sobre `repeat`, depois de FINISH_ARM_MS o BACK volta a
   * fechar o dialog.
   */
  const backDeliberado = (): boolean => {
    const agora = nowMs()
    const escapePorTempo =
      armRef.current.isAfterPause() && agora - lastBackAtRef.current >= FINISH_ARM_MS
    if (!armRef.current.isDeliberate() && !escapePorTempo) return false
    lastBackAtRef.current = agora
    return true
  }

  // Foco inicial sempre no botão de AVANÇO (spec: foco inicial óbvio, nunca
  // no Pular). Re-foca ao trocar de slide pra o cursor não ficar órfão.
  useEffect(() => {
    if (confirmExit) return
    setFocus(NEXT_KEY)
  }, [slide, confirmExit])

  // BACK (sempre ativo): com um dialog aberto, fecha; no slide 1 abre a
  // confirmação de sair (tela raiz); nos demais volta 1 slide.
  useBackKey(() => {
    // Segurar BACK não pode ESTROBAR a tela: a repetição abria o dialog de sair,
    // a seguinte fechava, e como o ConfirmDialog dá setFocus no mount e restaura
    // no unmount, o foco ia junto no pisca-pisca. Repetição de tecla presa é
    // IGNORADA; press deliberado age na hora (ver backDeliberado).
    if (!backDeliberado()) return

    if (exitFailed) {
      dismissExitFailed()
      return
    }
    if (confirmExit) {
      setConfirmExit(false)
      return
    }
    if (isFirst) {
      setConfirmExit(true)
    } else {
      // Mesma porta das setas: BACK também é passo de carrossel e também é
      // ritmado (segurar BACK não pode varrer os slides e cair no dialog de
      // sair antes do usuário ler o que apagou).
      goToSlide(slide - 1)
    }
  })

  /** Única porta de troca de slide (setas, OK e BACK passam por aqui): aplica o
   *  ritmo mínimo e desarma, pra a próxima repetição da MESMA tecla presa não
   *  emendar. Devolve se andou (o chamador não precisa saber por que não). */
  const goToSlide = (to: number): boolean => {
    if (to < 0 || to > SLIDE_COUNT - 1) return false
    const now = nowMs()
    if (!armRef.current.canStep(now)) return false
    armRef.current.slideChanged(now)
    setSlide(to)
    return true
  }

  const goNext = () => {
    if (isLast) {
      finish()
    } else {
      goToSlide(slide + 1)
    }
  }
  const goBack = () => {
    if (!isFirst) goToSlide(slide - 1)
  }

  // Terminar OU pular = onboarding "visto". Leva ao GATE DE CONTA OBRIGATÓRIA
  // (I5) — não há mais caminho anônimo. O gate é o "experimentar grátis".
  //
  // IRREVERSÍVEL: grava a flag por-device e o gate é raiz (o BACK de lá sai do
  // app, por desenho — gate-escape.test.ts). Então esta ação exige gesto
  // deliberado: nunca pode ser consequência do auto-repeat que trouxe o usuário
  // até o último slide (era o bug — 4 setas a 90ms apagavam o onboarding).
  const finish = () => {
    if (!armRef.current.canFinish(nowMs())) return
    markWelcomeSeen()
    navigate('/account/gate')
  }

  /** Cola entre a regra pura (welcome-nav) e os efeitos da tela. */
  const onArrow = (button: WelcomeButton) => (dir: ArrowDir) => {
    const action = resolveArrow(button, dir, slide)
    switch (action.kind) {
      case 'slide':
        goToSlide(action.to)
        return false
      case 'focus':
        setFocus(FOCUS_KEY[action.to])
        return false
      case 'edge':
        // Ponta do carrossel: a tecla é CONSUMIDA (não devolve pro spatial nav
        // caçar um focável que não existe e mover o foco pra lugar nenhum).
        return false
      default:
        return true
    }
  }

  const slideKey = `onboarding.slide${slide + 1}` as const

  return (
    <div
      className={`${styles.container} ${focusActive ? styles.focusActive : styles.focusCold}`}
    >
      <div className={styles.hero}>
        <AppLogo size={56} className={styles.appLogo} />
      </div>

      {/* Slide atual — arte + título + subtítulo. 1 ideia por slide.
          A arte conta a história (TV-first lê imagem antes de texto). */}
      <div className={styles.slide} key={slide}>
        <div className={styles.slideArt} aria-hidden="true">
          <div className={styles.sceneBox}>
            <WelcomeScene index={slide} />
          </div>
        </div>
        <h1 className={styles.slideTitle}>{t(`${slideKey}.title`)}</h1>
        <p className={styles.slideSubtitle}>{t(`${slideKey}.subtitle`)}</p>
      </div>

      {/* Dots de progresso — status, NÃO focáveis (anti foco-em-no-op). */}
      <div className={styles.dots} role="presentation">
        {Array.from({ length: SLIDE_COUNT }, (_, i) => (
          <span
            key={i}
            className={`${styles.dot} ${i === slide ? styles.dotActive : ''}`}
          />
        ))}
      </div>

      {/* Controles: Voltar (some no slide 1) + Próximo/Começar. ←/→ entre eles. */}
      <div className={styles.controls}>
        {!isFirst && (
          <Focusable
            focusKey={BACK_KEY}
            onEnterPress={goBack}
            onArrowPress={onArrow('back')}
            className={`${styles.btn} ${styles.btnSecondary}`}
          >
            <span className={styles.btnIcon} aria-hidden="true">
              <IconArrowLeft size={20} />
            </span>
            {t('onboarding.back')}
          </Focusable>
        )}

        <Focusable
          focusKey={NEXT_KEY}
          onEnterPress={goNext}
          onArrowPress={onArrow('next')}
          className={`${styles.btn} ${styles.btnPrimary}`}
        >
          {isLast ? t('onboarding.start') : t('onboarding.next')}
          <span className={styles.btnIcon} aria-hidden="true">
            {isLast ? <IconCheck size={20} /> : <IconNext size={20} />}
          </span>
        </Focusable>
      </div>

      {/* Pular — discreto, alcançável por ↓. Pular = visto (vai ao mesmo destino). */}
      <Focusable
        focusKey={SKIP_KEY}
        onEnterPress={finish}
        onArrowPress={onArrow('skip')}
        className={styles.skipLink}
      >
        {t('onboarding.skip')}
      </Focusable>

      {confirmExit && (
        <ConfirmDialog
          title={t('common.exitTitle')}
          message={t('common.exitBody')}
          variant="danger"
          confirmLabel={t('common.exit')}
          // Confirmar aqui NÃO navega e NÃO desmonta esta tela: pede a saída e
          // volta pra cá. Ver `shouldRestoreFocus`.
          //
          // NESTA TELA a declaração não conserta um dano observado — ela remove
          // uma DEPENDÊNCIA. Hoje o effect de `:57-60`
          // (`useEffect(..., [slide, confirmExit])`) já refoca `NEXT_KEY` quando
          // `confirmExit` cai pra false, que é exatamente o que o `onConfirm`
          // faz: o foco volta por acidente. Medido — mutar esta tela e rodar a
          // sonda dá VERDE.
          //
          // O que a flag troca: a cura deixa de depender da ORDEM em que o
          // effect da tela e o desmonte do dialog acontecem, e passa a ser
          // síncrona no mesmo tick do OK. E protege a tela se aquele effect for
          // removido num refactor de slides — quem mexer nos slides não tem
          // motivo nenhum pra saber que ele segurava o foco de um dialog.
          //
          // (O dano REAL da classe foi medido no AccountGateScreen, que não tem
          // effect de foco nenhum: 400 ms órfãos e as 4 setas em n=0 depois de
          // relançar pelo launcher.)
          originStaysMountedOnConfirm
          // Saída por CAPABILITY (platform-exit.ts). `window.close()` sozinho é
          // no-op em Tizen e em parte das TVs: o botão "Sair" não saía e o
          // usuário ficava sem rota de escape.
          onConfirm={() => {
            setConfirmExit(false)
            requestExit()
          }}
          // Gated pelo MESMO discriminador do BACK desta tela: o ConfirmDialog
          // tem useBackKey próprio e sem gate, então é aqui que a repetição
          // morre (ver backDeliberado).
          onCancel={() => {
            if (backDeliberado()) setConfirmExit(false)
          }}
        />
      )}

      {/* A plataforma não fechou o app: diz a verdade em vez de fingir que saiu. */}
      {exitFailed && (
        <ConfirmDialog
          title={t('gate.exitFailedTitle')}
          message={t('gate.exitFailedBody')}
          confirmLabel={t('common.gotIt')}
          hideCancel
          onConfirm={dismissExitFailed}
          onCancel={() => {
            if (backDeliberado()) dismissExitFailed()
          }}
        />
      )}
    </div>
  )
}
